import { type FormEvent, useState } from 'react';
import { Box, Button, Flex, Spinner, Text, theme } from '@optiaxiom/react';

import { MarkdownText } from '@frontend/components/MarkdownText';

import { useAskSteward } from '../hooks/useAskSteward';

/**
 * "Ask the Steward" — the single Stickyboard search/ask box. Enter sends the
 * query to the Steward, which answers directly or retrieves from the board
 * (RAG). Follow-ups continue the same thread; "New" resets it. Only the latest
 * answer is shown (Spotlight-style); the full thread lives in the agent's
 * memory.
 */
export function StickyboardSearchBar() {
  const { answer, isStreaming, error, submit, reset, ready, hasThread } = useAskSteward();
  const [value, setValue] = useState('');

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!value.trim() || isStreaming || !ready) return;
    submit(value);
    setValue('');
  };

  const showPanel = !!answer || isStreaming || !!error;

  return (
    <Box data-testid="stickyboard-search-bar" style={{ width: '100%' }}>
      <form onSubmit={onSubmit}>
        <Flex flexDirection="row" gap="8" alignItems="center">
          <input
            aria-label="Ask the Steward"
            placeholder={ready ? 'Ask the Steward…' : 'Steward unavailable'}
            value={value}
            onChange={e => setValue(e.target.value)}
            disabled={!ready}
            style={{
              flex: 1,
              height: 36,
              padding: '0 12px',
              borderRadius: 8,
              border: `1px solid ${theme.colors['border.tertiary']}`,
              background: theme.colors['bg.default'],
              color: theme.colors['fg.default'],
              outline: 'none',
              fontSize: 14,
            }}
          />
          <Button type="submit" size="sm" disabled={!ready || isStreaming || !value.trim()}>
            Ask
          </Button>
          {(hasThread || !!answer) && (
            <Button
              type="button"
              size="sm"
              appearance="subtle"
              aria-label="New conversation"
              onClick={() => {
                reset();
                setValue('');
              }}
            >
              New
            </Button>
          )}
        </Flex>
      </form>

      {showPanel && (
        <Box
          role="status"
          aria-live="polite"
          data-testid="stickyboard-answer"
          style={{
            marginTop: 8,
            padding: 12,
            borderRadius: 8,
            border: `1px solid ${theme.colors['border.tertiary']}`,
            background: theme.colors['bg.default'],
            maxHeight: 320,
            overflow: 'auto',
          }}
        >
          {error ? (
            <Text fontSize="sm" style={{ color: theme.colors['fg.error'] }}>
              {error}
            </Text>
          ) : answer ? (
            <MarkdownText text={answer} />
          ) : (
            <Flex flexDirection="row" gap="8" alignItems="center">
              <Spinner />
              <Text fontSize="sm" color="fg.tertiary">
                Asking the Steward…
              </Text>
            </Flex>
          )}
        </Box>
      )}
    </Box>
  );
}
